import React, { useState, useEffect, useMemo } from "react";
import { Button, Col, Container, Form, Input, Label, Pagination, PaginationItem, PaginationLink, Row, Table } from "reactstrap";
import debounce from 'lodash/debounce';

import { useTheme } from "../hooks/useTheme";
import { useSortData } from "../hooks/useSortData";

import DataLoaderFailed from "./loader/DataLoaderFailed";
import DataLoader from "./loader/DataLoader";
import SortIcon from "./SortIcon";
import DataFilter from "./DataFilter";
import Download from "./Download";

// this component queries the database by page number and filter criteria, fetching less records than the DataTable component
// it should be used for queries returning a large number of records
const DataTablePageQuery = ({
    columns,
    data,
    queryForDownload,
    isLoading,
    error,
    onRowClick,
    filters = [],
    actions = [],
    exportToCsv = true,
    csvFileName = "data.csv",
    sortConfiguration = { key: columns[0]?.key, direction: "ascending" },
    onFilterChange,
    totalRecords,
    downloadButtonIsEnabled,
    rowStyle
}) => {
    const theme = useTheme();

    // Advanced filters state and animations
    const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
    const resetAdvancedFilters = () => {
        filters = filters.forEach(f => {
            if (f.advanced)
                handleFilterChange(f, f.defaultValue);
        });
    }

    const mountedStyle = { animation: "inAnimation 250ms ease-in" };
    const unmountedStyle = { animation: "outAnimation 270ms ease-out", animationFillMode: "forwards" };

    // sorting custom hook
    const { sortBy, sortConfig } = useSortData(null, sortConfiguration);

    // Dynamic paging state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const numPerPageOptions = [10, 20, 50, 200];

    // Calculate total pages
    const totalPages = Math.ceil(totalRecords / pageSize);

    // Handler for filter changes
    const handleFilterChange = (filter, value) => {
        onFilterChange({ filterId: filter.id, value: value });
    };

    // Handler for items per page change
    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        onFilterChange({ filterId: "pageSize", value: e.target.value });
        setCurrentPage(1);  // resets the page to 1 every time the page size changes
    };

    // Handler for current page change
    const handleCurrentPageChange = (e, page) => {
        if (totalPages === 0) return;

        e.preventDefault();
        onFilterChange({ filterId: "pageNumber", value: page });
        setCurrentPage(page);
    }

    const handleSortOptionsChange = () => {
        handleFilterChange({ id: "sortOptions" }, sortConfig);
        setCurrentPage(1);
    }

    const debouncedSearchOptionsChange = useMemo(() =>
        debounce((value) => {
            onFilterChange({ filterId: "searchTerm", value: value })
            setCurrentPage(1);
        }, 500),
        [onFilterChange]
    );

    const handleSearchOptionsChange = (e) => {
        debouncedSearchOptionsChange(e.target.value);
    }

    useEffect(() => {
        return () => {
            debouncedSearchOptionsChange.cancel();
        };
    }, [debouncedSearchOptionsChange]);

    useEffect(() => {
        handleSortOptionsChange();
    }, [sortConfig]);

    return (
        <>
            <Container fluid className="px-0">
                <div
                    className="d-flex justify-content-between align-items-center flex-wrap gap-3"
                    style={{ minHeight: "40px" }}
                >
                    {/* Search Bar */}
                    <Form
                        style={{ flex: "1 1 auto", minWidth: "250px" }}
                        onSubmit={(e) => e.preventDefault()}
                    >
                        <Label className="visually-hidden" htmlFor="search">
                            Search
                        </Label>
                        <Input
                            type="search"
                            id="search"
                            placeholder={`Search ${totalRecords} records`}
                            onChange={handleSearchOptionsChange}
                            style={{ height: "38px" }}
                        />
                    </Form>

                    <div className="d-flex gap-2 flex-wrap align-items-center">
                        {filters
                            .filter((filter) => !filter.advanced)
                            .map((filter, index) => {
                                return (
                                    <DataFilter
                                        key={index}
                                        id={filter.id}
                                        label={filter.label}
                                        type={filter.type}
                                        placeholder={filter.placeholder}
                                        isSearchable={filter.isSearchable}
                                        options={filter.options}
                                        value={filter.type !== "select" ? filter.value : filter.options.find((opt) => opt.value === filter.value) || null}
                                        onChange={(value) => handleFilterChange(filter, value)}
                                        isInvalid={filter.isInvalid}
                                        defaultValue={filter.defaultValue}
                                        step={filter.step}
                                    />)
                            }
                            )
                        }
                    </div>

                    {/* Advanced Filters Toggle */}
                    {filters.filter((filter) => filter.advanced && !filter.isDateTimeRange
                    ).length > 0 && (
                            <Button
                                outline={!advancedFiltersOpen}
                                color={theme === "dark-mode" ? "dark" : "light"}
                                onClick={() => {
                                    setAdvancedFiltersOpen((prev) => !prev);
                                    resetAdvancedFilters();
                                }}
                            >
                                <i className="bi bi-funnel"></i>
                            </Button>
                        )}

                    <div style={{ flexGrow: 1 }}></div>

                    {exportToCsv && (<Download fileName={csvFileName} data={data} showIcon={false} alternativeQueryFunction={queryForDownload} text="Export" buttonIsEnabled={downloadButtonIsEnabled} />)}
                    {/* Action Buttons */}
                    <div className="d-flex gap-2">
                        {actions.map((action, index) => (
                            <div key={index}>{action}</div>
                        ))}
                    </div>
                </div>
            </Container>

            {/* Advanced Filters */}
            {advancedFiltersOpen && (
                <>
                    <br />
                    <Row>
                        <Container
                            fluid
                            style={advancedFiltersOpen ? mountedStyle : unmountedStyle}
                            className="contrast-dropdown py-3 transitionDiv"
                        >
                            <div style={{ minHeight: "40px", display: 'flex', flexDirection: 'row' }}>
                                <Col sm={6}>
                                    {filters
                                        .filter((filter) => filter.advanced)
                                        .map((filter, index) => {
                                            return (
                                                index % 2 === 0 && <DataFilter
                                                    key={index}
                                                    id={filter.id}
                                                    label={filter.label}
                                                    type={filter.type}
                                                    placeholder={filter.placeholder}
                                                    isSearchable={filter.isSearchable}
                                                    options={filter.options}
                                                    value={filter.type !== "select" ? filter.value : filter.options.find((opt) => opt.value === filter.value) || null}
                                                    onChange={(value) => handleFilterChange(filter, value)}
                                                    isInvalid={filter.isInvalid}
                                                    defaultValue={filter.defaultValue}
                                                    advanced={true}
                                                    step={filter.step}
                                                    size={filter.size}
                                                    isDisabled={filter.isDisabled}
                                                />
                                            )
                                        }
                                        )}
                                </Col>
                                <Col sm={6}>
                                    {filters
                                        .filter((filter) => filter.advanced)
                                        .map((filter, index) => {
                                            return (
                                                index % 2 === 1 && <DataFilter
                                                    key={index}
                                                    id={filter.id}
                                                    label={filter.label}
                                                    type={filter.type}
                                                    placeholder={filter.placeholder}
                                                    isSearchable={filter.isSearchable}
                                                    options={filter.options}
                                                    value={filter.type !== "select" ? filter.value : filter.options.find((opt) => opt.value === filter.value) || null}
                                                    onChange={(value) => handleFilterChange(filter, value)}
                                                    isInvalid={filter.isInvalid}
                                                    defaultValue={filter.defaultValue}
                                                    advanced={true}
                                                    step={filter.step}
                                                    isDisabled={filter.isDisabled}
                                                />
                                            )
                                        }
                                        )}
                                </Col>

                            </div>
                        </Container>
                    </Row>
                </>
            )}

            <Container fluid className="px-0">
                {error ? <DataLoaderFailed /> :
                    isLoading ?
                        <DataLoader />
                        :
                        <Table
                            className={
                                theme === "dark-mode"
                                    ? "no-wrap mt-3 align-middle table-dark"
                                    : "no-wrap mt-3 align-middle"
                            }
                            hover
                            responsive
                            striped
                        >
                            <thead>
                                <tr>
                                    {columns.map((col) => (
                                        <th key={col.key}
                                            style={col.isSortDisabled ? {} : { cursor: "pointer", ...(col.style || {}) }}
                                            onClick={() => !col.isSortDisabled && sortBy(col.key)}>
                                            <span style={{ display: "flex" }}>
                                                {col.label}
                                                &nbsp;
                                                {sortConfig?.key === col.key && !col.isSortDisabled && <SortIcon sort={sortConfig?.direction} />}
                                                {col.isSortDisabled && <i style={{ opacity: 0.2 }} className="bi bi-chevron-expand"></i>}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((item, index) => (
                                    <tr
                                        key={index}
                                        className={onRowClick ? "cursor-pointer" : ""}
                                        onClick={() => onRowClick && onRowClick(item)}
                                        style={{
                                            ...(onRowClick ? { cursor: "pointer" } : {}),
                                            ...(rowStyle ? (typeof rowStyle === 'function' ? rowStyle(item) : rowStyle) : {})
                                        }}
                                    >
                                        {columns.map((col) => (
                                            <td key={col.key} className={col.className || ""}>
                                                {col.render ? col.render(item[col.key], item) : item[col.key]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                }

                {/* Pagination Controls */}
                <Row className="mt-3 unlocked">
                    <Col className="d-flex justify-content-start">
                        <Pagination aria-label="Page navigation">
                            <PaginationItem disabled={totalPages === 0 || currentPage === 1}>
                                <PaginationLink
                                    first
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleCurrentPageChange(e, 1)
                                    }}
                                />
                            </PaginationItem>
                            <PaginationItem disabled={totalPages === 0 || currentPage === 1}>
                                <PaginationLink
                                    previous
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleCurrentPageChange(e, currentPage - 1)
                                    }}
                                />
                            </PaginationItem>
                            {(() => {
                                const pages = [];

                                const createPage = (pageNum) => (
                                    <PaginationItem key={pageNum} active={currentPage === pageNum}>
                                        <PaginationLink
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                //setCurrentPage(pageNum);
                                                handleCurrentPageChange(e, pageNum)
                                            }}
                                        >
                                            {pageNum}
                                        </PaginationLink>
                                    </PaginationItem>
                                );

                                const lastPage = totalPages;

                                if (lastPage <= 4) {
                                    // Just show all pages
                                    for (let i = 1; i <= lastPage; i++) {
                                        pages.push(createPage(i));
                                    }
                                } else {
                                    let startPage = currentPage;

                                    if (startPage > lastPage - 3) {
                                        // Clamp to last 4 pages
                                        startPage = lastPage - 3;
                                    }

                                    const visiblePages = [];
                                    for (let i = startPage; i < startPage + 3; i++) {
                                        visiblePages.push(i);
                                    }

                                    visiblePages.forEach((page) => pages.push(createPage(page)));

                                    if (startPage + 3 <= lastPage) {
                                        // Show final page or ellipsis
                                        if (startPage + 3 === lastPage) {
                                            pages.push(createPage(lastPage));
                                        } else {
                                            pages.push(
                                                <PaginationItem key="ellipsis" disabled>
                                                    <PaginationLink href="#">...</PaginationLink>
                                                </PaginationItem>
                                            );
                                        }
                                    }
                                }

                                return pages;
                            })()}
                            <PaginationItem disabled={totalPages === 0 || currentPage === totalPages}>
                                <PaginationLink
                                    next
                                    href="#"
                                    onClick={(e) => {
                                        handleCurrentPageChange(e, currentPage + 1)
                                    }}
                                />
                            </PaginationItem>
                            <PaginationItem disabled={totalPages === 0 || currentPage === totalPages}>
                                <PaginationLink
                                    last
                                    href="#"
                                    onClick={(e) => {
                                        handleCurrentPageChange(e, totalPages)
                                    }}
                                />
                            </PaginationItem>
                        </Pagination>
                        <div className="ps-3 p-2 d-flex justify-content-end">
                            <span className="text-muted">
                                {totalPages > 0 ? `Page ${currentPage} of ${totalPages}` : 'No records found'}
                            </span>
                        </div>
                    </Col>
                    <Col className="d-flex justify-content-end">
                        <Input
                            id="pageSizeSelect"
                            name="select"
                            type="select"
                            value={pageSize}
                            style={{ width: "5rem", height: "3rem" }}
                            bsSize="sm"
                            onChange={handlePageSizeChange}
                        >
                            {numPerPageOptions.map((num, index) => (
                                <option key={index} value={num}>
                                    {num}
                                </option>
                            ))}
                        </Input>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default DataTablePageQuery;
